/**
 * Login Component
 * 
 * Built with shadcn components.
 */

import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { TrendingUp, Loader2 } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { ApiServices } from '../../services/ApiServices'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Alert, AlertDescription } from '../../components/ui/alert'

const Login = () => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState('')
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    ApiServices.trackAuditEvent('page_view', 'login');
  }, []);

  useEffect(() => {
    // Check for URL parameters to see if user was redirected due to an expired session
    const params = new URLSearchParams(location.search)
    const reason = params.get('reason')

    if (reason === 'expired') {
      setSessionExpiredMessage('Ваша сессия истекла. Пожалуйста, войдите снова.')
    } else if (reason === 'unauthorized') {
      setSessionExpiredMessage('Пожалуйста, войдите, чтобы получить доступ к этой странице.')
    }
  }, [location.search])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSessionExpiredMessage('')
    setLoading(true)

    try {
      const response = await ApiServices.login({ username, password })

      if (response.access_token) {
        // Store token first so getCurrentUser can use it
        localStorage.setItem('authToken', response.access_token)

        // Fetch full user data
        let userData = { username }
        try {
          const userResponse = await ApiServices.getCurrentUser()
          if (userResponse && userResponse.user) {
            userData = userResponse.user
          }
        } catch (userError) {
          console.warn('Could not fetch user details:', userError)
        }

        login(userData, response.access_token)

        // Redirect to the page they were trying to access, or home
        const from = location.state?.from?.pathname || '/'
        navigate(from, { replace: true })
      } else {
        setError('Неверное имя пользователя или пароль.')
      }
    } catch (err) {
      console.error('Login error:', err)
      setError(err.message || 'Не удалось войти. Пожалуйста, попробуйте позже.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background overflow-hidden">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">Trends</CardTitle>
          <CardDescription>Войдите в свой аккаунт</CardDescription>
        </CardHeader>

        <CardContent>
          {sessionExpiredMessage && (
            <Alert className="mb-4">
              <AlertDescription>{sessionExpiredMessage}</AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="username" className="text-sm font-medium">
                Имя пользователя
              </label>
              <Input
                id="username"
                type="text"
                placeholder="Введите имя пользователя"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
                required
                autoComplete="username"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                Пароль
              </label>
              <Input
                id="password"
                type="password"
                placeholder="Введите пароль"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
                autoComplete="current-password"
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Вход...
                </>
              ) : (
                'Войти'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

export default Login